import { createContext, useContext, type ReactNode } from 'react';

import { type ApiClient } from './client.js';

/**
 * Makes the one `ApiClient` reachable from a hook.
 *
 * There is exactly one client per application, holding the access token and
 * the single-flight refresh. Importing a module-scope singleton would work in
 * a browser and break every test, because two tests in one file would share a
 * token; passing it through context keeps the singleton out of the module
 * graph and lets a test hand in a client wired to a fake `fetch`.
 */
const ApiContext = createContext<ApiClient | null>(null);

export function ApiProvider({
  client,
  children,
}: {
  readonly client: ApiClient;
  readonly children: ReactNode;
}): React.JSX.Element {
  return <ApiContext.Provider value={client}>{children}</ApiContext.Provider>;
}

export function useApi(): ApiClient {
  const client = useContext(ApiContext);
  if (client === null) {
    throw new Error('useApi must be used inside an <ApiProvider>');
  }
  return client;
}
